import { IIntent } from '@lifetools/shared-app-data';
import { ScheduledBlock } from 'types/ScheduledBlock';
import { LayoutConfig } from './LayoutConfig';
import { layoutSchedule } from './layoutSchedule';
import { scheduleLength } from './scheduleLength';
import { StoredScheduleBlock } from './StoredScheduleBlock';

/**
 * The semantic result of a drag gesture: which block the user picked up and the
 * slot (minutes from the schedule start) they are pointing at.
 */
export interface DragIntention {
  /**
   * The todo id of the block being dragged.
   */
  activeTodoId: string;

  /**
   * The slot offset the pointer is over. The drop target is the block whose
   * rendered layout covers this slot.
   */
  targetOffset: number;
}

export interface ApplyDragContext {
  intentsByTodoId: Record<string, IIntent>;
  config: LayoutConfig;
}

interface Pin {
  start: number;
  end: number;
}

/**
 * Applies a drag to the stored schedule model, returning the new model — the
 * placement half of the displacement spec, computed entirely in `{todo, start}`
 * space. Rendering (covers, conflicts, empties) is NOT computed here: the drag
 * preview is `layoutSchedule(applyDrag(model, ...))`, and the drop persists the
 * returned model directly, so the preview and the persisted result are the same
 * value by construction.
 *
 * The displacement spec (unchanged from `rescheduleViaDrag`):
 *
 *  - Dragging later: the affected span runs from the active block's original start
 *    to the over block's RENDERED end (the rows the user points at). Dragging
 *    earlier: from the over block's original start to the active block's TRUE end
 *    (its full duration must fit in the span).
 *  - Only blocks whose original layout intersects the span move; everything else
 *    keeps its exact stored start.
 *  - In-span movable blocks keep their relative order and cursor-pack into the
 *    vacated time; the active block is justified to the far end of the span.
 *  - Appointments are pinned: they never move, and a block that cannot fully fit
 *    before one keeps its start (the wrap rule) — the overflow renders as empties
 *    and a `scheduleAround` conflict, both computed by the layout engine.
 *  - Overruns never displace post-span blocks: an overrunning block keeps the
 *    start the cursor walk gave it and the layout engine truncates/conflict-marks
 *    it against whatever actually follows.
 *
 * No-op drags (unknown blocks, active === target, appointment as active or
 * target) return the INPUT model, reference-equal, so callers can cheaply detect
 * that there is nothing to preview or persist.
 */
export function applyDrag(
  model: StoredScheduleBlock[],
  { activeTodoId, targetOffset }: DragIntention,
  { intentsByTodoId, config }: ApplyDragContext,
): StoredScheduleBlock[] {
  const { startTime, interval } = config;
  const endOffset = scheduleLength(config);

  // The geometry the spec is defined over: the CURRENT rendered layout.
  const layout = layoutSchedule(model, intentsByTodoId, config);

  const blockTodoId = (block: ScheduledBlock): string | undefined =>
    block.intent?.todo?.id != null ? `${block.intent.todo.id}` : undefined;
  const isAppointment = (block: ScheduledBlock): boolean =>
    block.intent?.todo?.type === 'appointment';

  const renderedEndOf = (block: ScheduledBlock): number =>
    block.startOffset + block.times.length * interval;
  const trueDurationOf = (block: ScheduledBlock): number =>
    block.intent?.duration ?? interval;
  const trueEndOf = (block: ScheduledBlock): number =>
    block.startOffset + Math.ceil(trueDurationOf(block) / interval) * interval;

  const active = layout.find(block => blockTodoId(block) === activeTodoId);
  const over = layout.find(block =>
    block.startOffset <= targetOffset && targetOffset < renderedEndOf(block)
  );

  if (
    !active || !over ||
    over === active || over.startOffset === active.startOffset ||
    isAppointment(active) || isAppointment(over)
  ) {
    return model;
  }

  const activeOffset = active.startOffset;
  const overOffset = over.startOffset;
  const draggingLater = overOffset > activeOffset;

  const spanStart = draggingLater ? activeOffset : overOffset;
  const spanEnd = draggingLater ? renderedEndOf(over) : trueEndOf(active);

  // Pins: ALL appointments, not just in-span ones — a block placed near the span
  // end can reach an appointment just outside the span and must still wrap.
  const pins: Pin[] = layout
    .filter(isAppointment)
    .map(block => ({ start: block.startOffset, end: trueEndOf(block) }))
    .sort((a, b) => a.start - b.start);

  // In-span movable blocks (original layout intersects the span), in start order.
  const inSpan = layout.filter(block =>
    !!block.intent && !isAppointment(block) &&
    block.startOffset < spanEnd && trueEndOf(block) > spanStart
  );
  const inBetween = inSpan.filter(block => blockTodoId(block) !== activeTodoId);
  const ordered = draggingLater ? [...inBetween, active] : [active, ...inBetween];

  const roundUp = (minutes: number): number => Math.ceil(minutes / interval) * interval;

  // Dragging later, the active is justified to END at the span end and reserves
  // that tail for itself; the cursor walk may not push an in-between block into it.
  const reservedTailStart = draggingLater
    ? spanEnd - roundUp(trueDurationOf(active))
    : spanEnd;

  // --- Cursor-place the ordered blocks, wrapping pins; collect the new starts. ---
  const newStarts = new Map<string, number>();
  const takenStarts = new Set<number>();

  // Out-of-span blocks (and pins) keep their starts; those starts are taken.
  for (const block of layout) {
    if (!block.intent) continue;
    if (isAppointment(block) || !inSpan.includes(block)) {
      takenStarts.add(block.startOffset);
    }
  }

  let cursor = spanStart;

  for (const block of ordered) {
    const todoId = blockTodoId(block)!;
    const duration = trueDurationOf(block);
    const isActive = todoId === activeTodoId;

    let start = cursor;
    if (isActive && draggingLater) {
      start = Math.max(cursor, spanEnd - duration);
    }

    const overrunLimit = isActive ? spanEnd : reservedTailStart;
    const placed = placeWithWrap(start, duration, pins, overrunLimit, endOffset, interval, takenStarts);

    newStarts.set(todoId, placed.start);
    takenStarts.add(placed.start);
    cursor = placed.nextCursor;
  }

  // --- Emit the new model: every stored block, moved blocks with new starts. ---
  let changed = false;
  const next = model.map(entry => {
    const newOffset = newStarts.get(entry.todo);

    if (newOffset === undefined) {
      return entry;
    }

    const newStart = startTime.plusMinutes(newOffset).toString();

    if (entry.start === newStart) {
      return entry;
    }

    changed = true;
    return { ...entry, start: newStart };
  });

  return changed ? next : model;
}

/**
 * The cursor walk for one block: where does a block of `duration` starting at
 * `start` actually begin, given the pinned appointments, and where does the
 * cursor resume for the next block?
 *
 *  - Overlapping a pin with room before it: the block keeps its start (the wrap
 *    rule — the layout engine renders the truncation); the cursor resumes after
 *    the pin plus the overflow, so the next block starts as if this one wrapped.
 *  - Starting at/inside a pin: slide to the pin's end and retry (another pin may
 *    immediately follow).
 *  - Overrunning `overrunLimit` (the reserved tail for in-between blocks when
 *    dragging later; the span end otherwise) or the schedule end: the block keeps
 *    its start and the cursor advances only to the limit — post-span blocks are
 *    never displaced. If the start itself sits at/past the limit (no slot of the
 *    block fits), it is pinned to the nearest free start below the limit that no
 *    other block occupies, so the persisted model never carries duplicate starts.
 */
function placeWithWrap(
  start: number,
  duration: number,
  pins: Pin[],
  overrunLimit: number,
  endOffset: number,
  interval: number,
  takenStarts: Set<number>,
): { start: number; nextCursor: number } {
  const pin = pins.find(candidate => start < candidate.end && start + duration > candidate.start);

  if (pin) {
    const fit = pin.start - start;

    if (fit <= 0) {
      return placeWithWrap(pin.end, duration, pins, overrunLimit, endOffset, interval, takenStarts);
    }

    return { start, nextCursor: pin.end + (duration - fit) };
  }

  const end = start + duration;

  if (end > overrunLimit || end > endOffset) {
    const limit = Math.min(overrunLimit, endOffset);
    let placedStart = start;

    // No slot of the block fits before the limit: park it at the nearest free
    // start below the schedule end that isn't a pin slot or another block's start.
    if (limit - start <= 0) {
      const isBlocked = (offset: number): boolean =>
        pins.some(candidate => offset >= candidate.start && offset < candidate.end) ||
        takenStarts.has(offset);
      let candidate = Math.min(limit, endOffset - interval);
      while (candidate > 0 && isBlocked(candidate)) {
        candidate -= interval;
      }
      placedStart = Math.max(0, candidate);
    }

    return { start: placedStart, nextCursor: overrunLimit };
  }

  return { start, nextCursor: end };
}
